import React, { useEffect, useState } from 'react';
import { MapPin, Plus, Pencil, Trash2, Check, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

function LocationRow({ loc, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: loc.name, description: loc.description || '', address: loc.address || '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(loc.id, form);
    setSaving(false);
    setEditing(false);
  };

  if (editing) return (
    <tr style={{ background: '#F0F7FF' }}>
      <td style={{ padding: '10px 16px' }}>
        <input className="form-input" style={{ fontSize: 13 }} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
      </td>
      <td style={{ padding: '10px 12px' }}>
        <input className="form-input" style={{ fontSize: 13 }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Descripción..." />
      </td>
      <td style={{ padding: '10px 12px' }}>
        <input className="form-input" style={{ fontSize: 13 }} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Dirección..." />
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving} style={{ marginRight: 6 }}>
          <Check size={13} /> {saving ? '...' : 'Guardar'}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>
          <X size={13} />
        </button>
      </td>
    </tr>
  );

  return (
    <tr>
      <td style={{ padding: '12px 16px', fontWeight: 600, fontSize: 14 }}>
        <MapPin size={14} style={{ marginRight: 6, color: 'var(--accent)', verticalAlign: 'middle' }} />
        {loc.name}
      </td>
      <td style={{ padding: '12px 12px', fontSize: 13, color: 'var(--ink-2)' }}>{loc.description || '—'}</td>
      <td style={{ padding: '12px 12px', fontSize: 13, color: 'var(--ink-2)' }}>{loc.address || '—'}</td>
      <td style={{ padding: '12px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
        <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)} style={{ marginRight: 6 }}>
          <Pencil size={12} />
        </button>
        <button className="btn btn-danger btn-sm" onClick={() => onDelete(loc.id, loc.name)}>
          <Trash2 size={12} />
        </button>
      </td>
    </tr>
  );
}

export default function Locations() {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', description: '', address: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    fetch(`${API_URL}/locations`, { headers })
      .then(r => r.json())
      .then(data => setLocations(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newForm.name.trim()) { setError('El nombre es obligatorio.'); return; }
    setSaving(true); setError('');
    try {
      const r = await fetch(`${API_URL}/locations`, { method: 'POST', headers, body: JSON.stringify(newForm) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      setAdding(false);
      setNewForm({ name: '', description: '', address: '' });
      load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleSave = async (id, form) => {
    const r = await fetch(`${API_URL}/locations/${id}`, { method: 'PATCH', headers, body: JSON.stringify(form) });
    if (r.ok) load();
    else { const d = await r.json(); alert(d.error); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar la ubicación "${name}"?`)) return;
    const r = await fetch(`${API_URL}/locations/${id}`, { method: 'DELETE', headers });
    const d = await r.json();
    if (r.ok) load();
    else alert(d.error);
  };

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ background: '#E6F1FB', borderRadius: 10, padding: 8 }}>
            <MapPin size={20} color="var(--accent)" />
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>Ubicaciones</div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>Sedes y sucursales de la organización</div>
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => { setAdding(true); setError(''); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={15} /> Nueva ubicación
        </button>
      </div>

      {adding && (
        <div className="card" style={{ padding: '18px 20px', marginBottom: 16, border: '2px solid #0063A5' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: 'var(--accent)' }}>Nueva ubicación</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Nombre <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className="form-input" value={newForm.name} onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Sucursal Norte" autoFocus />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Descripción</label>
              <input className="form-input" value={newForm.description} onChange={e => setNewForm(f => ({ ...f, description: e.target.value }))} placeholder="Ej: Piso 3, Edificio A" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Dirección</label>
              <input className="form-input" value={newForm.address} onChange={e => setNewForm(f => ({ ...f, address: e.target.value }))} placeholder="Ej: Av. Corrientes 1234" />
            </div>
          </div>
          {error && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 10 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={saving}>
              {saving ? 'Guardando...' : '+ Agregar'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setAdding(false); setError(''); }}>Cancelar</button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : locations.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 14 }}>
            No hay ubicaciones registradas.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase' }}>Nombre</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase' }}>Descripción</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase' }}>Dirección</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((loc, i) => (
                <React.Fragment key={loc.id}>
                  {i > 0 && <tr><td colSpan={4} style={{ borderTop: '1px solid var(--bg)', padding: 0 }} /></tr>}
                  <LocationRow loc={loc} onSave={handleSave} onDelete={handleDelete} />
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
